/*
 * Purpose: Node configuration side panel for selected workflow nodes
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useMemo, useState, useCallback } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { listResolvableSecretNames } from '@/lib/resolveSecret';
import { useSecretStore } from '@/stores/secretStore';
import { X, Loader2, Sparkles } from 'lucide-react';
import { NodeInfo } from './NodeInfo';
import { ValidationPanel } from './ValidationPanel';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import { BlockContextualConfigPanel } from '@/components/blocks/BlockContextualConfigPanel';
import { FlowSummaryPanel } from '@/components/FlowSummaryPanel';
import { planAndApplyGoalToMap } from '@/lib/applyPlanToMap';
import { toast } from 'sonner';
import { AGENT_BLOCK_HELP, DRONE_BLOCK_HELP } from '@/lib/blockContext';
import { PRODUCT_BRAND } from '@/lib/productTerms';
import { BDNA_LOCK_COLORS, getBrandedBlockDef, BRANDED_IDEA_HELP, brandedBuildStatus } from '@/lib/brandedBlocks';

interface WorkflowPanelProps {
  onClose?: () => void;
  onOpenSmartInput?: () => void;
}

export function WorkflowPanel({ onClose, onOpenSmartInput }: WorkflowPanelProps) {
  const { currentWorkflow, selectedNode, updateNode, selectNode } = useWorkflowStore();
  const vaultUnlocked = useSecretStore((s) => s.isUnlocked);
  const vaultSecretCount = useSecretStore((s) => s.secrets.length);
  const secretNames = useMemo(
    () => listResolvableSecretNames(),
    [vaultUnlocked, vaultSecretCount]
  );

  const handleClose = () => {
    selectNode(null);
    onClose?.();
  };

  if (!currentWorkflow) {
    return null;
  }

  if (!selectedNode) {
    return <FlowSummaryPanel />;
  }

  const node = currentWorkflow.nodes.find((n) => n.id === selectedNode);
  if (!node) {
    return <FlowSummaryPanel />;
  }

  const handleConfigChange = (key: string, value: any) => {
    updateNode(node.id, { [key]: value });
  };

  const incomingEdges = currentWorkflow.edges.filter((e) => e.to === node.id);
  const outgoingEdges = currentWorkflow.edges.filter((e) => e.from === node.id);

  return (
    <div className="h-full min-h-0 border-l border-border bg-card overflow-hidden flex flex-col">
      <div className="p-4 border-b border-border shrink-0 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground truncate">{getNodeLabel(node)}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {getBlockTypeLabel(node.type, node.subtype)}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={handleClose}
          aria-label="Close configuration panel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 p-4 space-y-4 overflow-auto min-h-0">
        <BlockContextualConfigPanel
          node={node}
          onUpdate={handleConfigChange}
          onOpenSmartInput={onOpenSmartInput}
        />
        <NodeInfo node={node} incomingConnections={incomingEdges.length} outgoingConnections={outgoingEdges.length} />
        <ValidationPanel />
        {node.type === 'start' && node.subtype === 'mission' && (
          <StartMissionConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'goal' && node.subtype === 'outcome' && (
          <GoalOutcomeConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'agent' && node.subtype === 'assistant' && (
          <AgentBlockConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'drone' && node.subtype === 'remote' && (
          <DroneBlockConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'branded' && (
          <BrandedBlockConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'trigger' && node.subtype === 'gmail_inbound' && (
          <GmailInboundTriggerConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'trigger' && node.subtype === 'webhook' && (
          <WebhookTriggerConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'trigger' && node.subtype === 'manual' && (
          <ManualTriggerConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'trigger' && node.subtype === 'timer' && (
          <TimerTriggerConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'tool' && node.subtype === 'http' && (
          <HttpToolConfig node={node} onUpdate={handleConfigChange} secretNames={secretNames} />
        )}

        {node.type === 'tool' && node.subtype === 'transform' && (
          <TransformToolConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'action' && node.subtype === 'notification' && (
          <NotificationActionConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'action' && node.subtype === 'log' && (
          <LogActionConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'tool' && node.subtype === 'conditional' && (
          <ConditionalToolConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'tool' && node.subtype === 'delay' && (
          <DelayToolConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'trigger' && node.subtype === 'api' && (
          <ApiTriggerConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'action' && node.subtype === 'webhook' && (
          <WebhookActionConfig node={node} onUpdate={handleConfigChange} />
        )}

        {node.type === 'action' && node.subtype === 'storage' && (
          <StorageActionConfig node={node} onUpdate={handleConfigChange} />
        )}
      </div>
    </div>
  );
}

function configText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function StartMissionConfig({ node, onUpdate }: { node: { config: Record<string, unknown> }; onUpdate: (k: string, v: unknown) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Start label</Label>
        <Input
          placeholder="Start"
          value={configText(node.config.name)}
          onChange={(e) => onUpdate('name', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Every Mission has exactly one START block. Connect it to your GOAL or first operational step.
      </p>
    </div>
  );
}

function GoalOutcomeConfig({
  node,
  onUpdate,
}: {
  node: { config: Record<string, unknown> };
  onUpdate: (k: string, v: unknown) => void;
}) {
  const [wiring, setWiring] = useState(false);
  const outcome = configText(node.config.outcome);

  const wireToMap = useCallback(async () => {
    const trimmed = outcome.trim();
    if (trimmed.length < 12) {
      toast.error('Describe the outcome in a sentence or two first.');
      return;
    }
    setWiring(true);
    try {
      onUpdate('outcome', trimmed);
      const { workflow, plan } = await planAndApplyGoalToMap(trimmed);
      useWorkflowStore.getState().saveWorkflow();
      toast.success(`Wired ${workflow?.nodes.length ?? plan?.steps.length ?? 0} blocks to your map`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not wire blocks to map';
      toast.error(msg, { action: { label: 'Retry', onClick: () => void wireToMap() } });
    } finally {
      setWiring(false);
    }
  }, [onUpdate, outcome]);

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Identified outcome</Label>
        <textarea
          placeholder="What should happen automatically when this Mission succeeds?"
          value={outcome}
          onChange={(e) => onUpdate('outcome', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={4}
        />
      </div>
      <div>
        <Label className="text-sm">Display name (optional)</Label>
        <Input
          placeholder="e.g. Weekly job digest"
          value={configText(node.config.name)}
          onChange={(e) => onUpdate('name', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <Button
        type="button"
        size="sm"
        className="w-full gap-2"
        disabled={wiring || outcome.trim().length < 12}
        onClick={() => void wireToMap()}
      >
        {wiring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        Wire blocks to map
      </Button>
      <p className="text-xs text-muted-foreground">
        Goal text drives which blocks appear — each step gets intent and task outcome for MCP and connectors.
      </p>
    </div>
  );
}

function AgentBlockConfig({ node, onUpdate }: { node: { config: Record<string, unknown> }; onUpdate: (k: string, v: unknown) => void }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{AGENT_BLOCK_HELP}</p>
      <div>
        <Label className="text-sm">Agent brief</Label>
        <textarea
          placeholder="What should this agent reason about and delegate in this Mission?"
          value={configText(node.config.agentBrief)}
          onChange={(e) => {
            onUpdate('agentBrief', e.target.value);
            onUpdate('contextPurpose', e.target.value);
          }}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={3}
        />
      </div>
      <div>
        <Label className="text-sm">Autonomy</Label>
        <select
          className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
          value={configText(node.config.autonomy) || 'supervised'}
          onChange={(e) => onUpdate('autonomy', e.target.value)}
        >
          <option value="supervised">Supervised — human approves major steps</option>
          <option value="autonomous">Autonomous — may replan map if unlocked</option>
        </select>
      </div>
    </div>
  );
}

function DroneBlockConfig({ node, onUpdate }: { node: { config: Record<string, unknown> }; onUpdate: (k: string, v: unknown) => void }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{DRONE_BLOCK_HELP}</p>
      <div>
        <Label className="text-sm">Drone task</Label>
        <textarea
          placeholder="What should this drone execute remotely in this Mission?"
          value={configText(node.config.droneTask)}
          onChange={(e) => {
            onUpdate('droneTask', e.target.value);
            onUpdate('contextPurpose', e.target.value);
          }}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={3}
        />
      </div>
      <div>
        <Label className="text-sm">Remote target (optional)</Label>
        <Input
          placeholder="URL, worker id, or service name"
          value={configText(node.config.remoteTarget)}
          onChange={(e) => onUpdate('remoteTarget', e.target.value)}
          className="mt-1 text-xs font-mono"
        />
      </div>
    </div>
  );
}

function BrandedBlockConfig({
  node,
  onUpdate,
}: {
  node: { subtype: string; config: Record<string, unknown> };
  onUpdate: (k: string, v: unknown) => void;
}) {
  const def = getBrandedBlockDef(node.subtype);
  if (!def) return null;

  const status = brandedBuildStatus(node.subtype, node.config);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground border-l-2 border-amber-500/50 pl-2">{BRANDED_IDEA_HELP}</p>
      <p className="text-xs text-muted-foreground">{def.description}</p>
      {status === 'idea' ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Status: Idea — fill contextual configuration purpose (or block fields below) so Director can build this.
        </p>
      ) : (
        <p className="text-xs text-green-700 dark:text-green-400">
          Purpose clear — Director may wire real behavior on the next build pass.
        </p>
      )}

      {node.subtype === 'bdna_lock' && (
        <>
          <div>
            <Label className="text-sm">Lock color</Label>
            <select
              className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
              value={configText(node.config.lockColor) || 'teal'}
              onChange={(e) => onUpdate('lockColor', e.target.value)}
            >
              {Object.entries(BDNA_LOCK_COLORS).map(([key, c]) => (
                <option key={key} value={key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-sm">BDNA profile</Label>
            <Input
              placeholder="Identity or permission profile"
              value={configText(node.config.bdnaProfile)}
              onChange={(e) => onUpdate('bdnaProfile', e.target.value)}
              className="mt-1 text-xs"
            />
          </div>
        </>
      )}

      {node.subtype === 'squadbox_mega' && (
        <>
          <div>
            <Label className="text-sm">Squad brief</Label>
            <textarea
              placeholder="What squad should this mega block orchestrate?"
              value={configText(node.config.squadBrief)}
              onChange={(e) => onUpdate('squadBrief', e.target.value)}
              className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
              rows={3}
            />
          </div>
          <div>
            <Label className="text-sm">Squad size</Label>
            <Input
              type="number"
              min={1}
              max={12}
              value={configText(node.config.squadSize) || '3'}
              onChange={(e) => onUpdate('squadSize', parseInt(e.target.value, 10))}
              className="mt-1 text-xs"
            />
          </div>
        </>
      )}

      {node.subtype === 'thrpy_dictation' && (
        <>
          <div>
            <Label className="text-sm">Dictation mode</Label>
            <select
              className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
              value={configText(node.config.dictationMode) || 'clinical'}
              onChange={(e) => onUpdate('dictationMode', e.target.value)}
            >
              <option value="clinical">Clinical</option>
              <option value="personal">Personal</option>
              <option value="notes">Session notes</option>
            </select>
          </div>
          <div>
            <Label className="text-sm">Language</Label>
            <Input
              value={configText(node.config.language) || 'en'}
              onChange={(e) => onUpdate('language', e.target.value)}
              className="mt-1 text-xs font-mono"
            />
          </div>
        </>
      )}

      {node.subtype === 'sensaffect_emotion' && (
        <>
          <div>
            <Label className="text-sm">Sensitivity</Label>
            <select
              className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
              value={configText(node.config.sensitivity) || 'balanced'}
              onChange={(e) => onUpdate('sensitivity', e.target.value)}
            >
              <option value="low">Low</option>
              <option value="balanced">Balanced</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <Label className="text-sm">Output format</Label>
            <select
              className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
              value={configText(node.config.outputFormat) || 'scores'}
              onChange={(e) => onUpdate('outputFormat', e.target.value)}
            >
              <option value="scores">Emotion scores</option>
              <option value="labels">Primary label</option>
              <option value="full">Full affect map</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
}

function GmailInboundTriggerConfig({ node, onUpdate }: any) {
  const inboxId = configText(node.config.inboxId);
  const postUrl = configText(node.config.postUrl);
  const source = configText(node.config.source) || 'webhook';

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Source</Label>
        <select
          className="mt-1 w-full text-xs border rounded-md px-2 py-2 bg-background"
          value={source}
          onChange={(e) => onUpdate('source', e.target.value)}
        >
          <option value="gmail_api">Your Gmail (OAuth — Connect Gmail)</option>
          <option value="webhook">Edge inbox (webhook — advanced)</option>
        </select>
      </div>
      {source === 'gmail_api' ? (
        <div>
          <Label className="text-sm">Gmail search query</Label>
          <Input
            placeholder="from:linkedin.com subject:job newer_than:7d"
            value={configText(node.config.gmailQuery)}
            onChange={(e) => onUpdate('gmailQuery', e.target.value)}
            className="mt-1 text-xs font-mono"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Searches your real Gmail inbox — same syntax as Gmail search bar.
          </p>
        </div>
      ) : (
        <>
          <div>
            <Label className="text-sm">Inbox ID</Label>
            <Input
              placeholder={`Created by ${PRODUCT_BRAND} or Create inbox`}
              value={inboxId}
              onChange={(e) => onUpdate('inboxId', e.target.value)}
              className="mt-1 text-xs font-mono"
            />
          </div>
          <div>
            <Label className="text-sm">Webhook POST URL</Label>
            <Input
              readOnly
              value={postUrl || (inboxId ? `/api/webhooks/gmail/${inboxId}` : '')}
              className="mt-1 text-xs font-mono"
            />
          </div>
        </>
      )}
      <p className="text-xs text-muted-foreground">
        Dry run uses fixtures. Live Run uses webhook poll or Gmail API per source above.
      </p>
    </div>
  );
}

function WebhookTriggerConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Webhook URL</Label>
        <Input
          placeholder="https://your-domain.com/webhook"
          value={configText(node.config.url)}
          onChange={(e) => onUpdate('url', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <Card className="p-2 bg-muted/50 text-xs text-muted-foreground">
        <p className="font-mono break-all">
          {configText(node.config.url) || 'URL will be shown here'}
        </p>
      </Card>
    </div>
  );
}

function ManualTriggerConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Trigger Name</Label>
        <Input
          placeholder="e.g., Daily Report"
          value={node.config.name || ''}
          onChange={(e) => onUpdate('name', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        This workflow will be triggered manually by clicking the Run button.
      </p>
    </div>
  );
}

function TimerTriggerConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Interval (seconds)</Label>
        <Input
          type="number"
          placeholder="3600"
          value={node.config.interval || ''}
          onChange={(e) => onUpdate('interval', parseInt(e.target.value))}
          className="mt-1 text-xs"
        />
      </div>
    </div>
  );
}

function HttpToolConfig({ node, onUpdate, secretNames }: { node: any; onUpdate: (k: string, v: any) => void; secretNames: string[] }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Method</Label>
        <select
          value={node.config.method || 'GET'}
          onChange={(e) => onUpdate('method', e.target.value)}
          className="w-full mt-1 text-xs px-2 py-1 rounded border border-input bg-background"
        >
          <option>GET</option>
          <option>POST</option>
          <option>PUT</option>
          <option>DELETE</option>
        </select>
      </div>
      <div>
        <Label className="text-sm">URL</Label>
        <Input
          placeholder="https://api.example.com/endpoint"
          value={configText(node.config.url)}
          onChange={(e) => onUpdate('url', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <div>
        <Label className="text-sm">Headers (JSON)</Label>
        <textarea
          placeholder='{"Authorization": "Bearer YOUR_KEY"}'
          value={configText(node.config.headers)}
          onChange={(e) => onUpdate('headers', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={3}
        />
      </div>
      {secretNames.length > 0 && (
        <div className="p-2 bg-muted/50 rounded text-xs">
          <p className="font-medium mb-1">Vault secrets ($NAME in headers):</p>
          <div className="space-y-1">
            {secretNames.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => onUpdate('headers', `${configText(node.config.headers)}\n$${name}`)}
                className="block w-full text-left px-2 py-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                ${name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TransformToolConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Transformation (JavaScript)</Label>
        <textarea
          placeholder="return input.map(item => ({ ...item, processed: true }))"
          value={node.config.transform || ''}
          onChange={(e) => onUpdate('transform', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background font-mono"
          rows={4}
        />
      </div>
    </div>
  );
}

function NotificationActionConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Message</Label>
        <textarea
          placeholder="Your notification message"
          value={node.config.message || ''}
          onChange={(e) => onUpdate('message', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={3}
        />
      </div>
      <div>
        <Label className="text-sm">Type</Label>
        <select
          value={node.config.type || 'info'}
          onChange={(e) => onUpdate('type', e.target.value)}
          className="w-full mt-1 text-xs px-2 py-1 rounded border border-input bg-background"
        >
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
        </select>
      </div>
    </div>
  );
}

function LogActionConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Log Message</Label>
        <textarea
          placeholder="Message to log"
          value={node.config.message || ''}
          onChange={(e) => onUpdate('message', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={3}
        />
      </div>
    </div>
  );
}

function ConditionalToolConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Condition (JavaScript)</Label>
        <textarea
          placeholder="input.status === 'success'"
          value={node.config.condition || ''}
          onChange={(e) => onUpdate('condition', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background font-mono"
          rows={3}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        If true, passes input to next node. If false, stops execution chain.
      </p>
    </div>
  );
}

function DelayToolConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Duration (milliseconds)</Label>
        <Input
          type="number"
          placeholder="1000"
          value={node.config.duration || ''}
          onChange={(e) => onUpdate('duration', parseInt(e.target.value))}
          className="mt-1 text-xs"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Pause execution for specified duration before continuing.
      </p>
    </div>
  );
}

function ApiTriggerConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">API Endpoint</Label>
        <Input
          placeholder="https://api.example.com/trigger"
          value={node.config.endpoint || ''}
          onChange={(e) => onUpdate('endpoint', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <div>
        <Label className="text-sm">Expected Content Type</Label>
        <select
          value={node.config.contentType || 'application/json'}
          onChange={(e) => onUpdate('contentType', e.target.value)}
          className="w-full mt-1 text-xs px-2 py-1 rounded border border-input bg-background"
        >
          <option>application/json</option>
          <option>application/x-www-form-urlencoded</option>
          <option>text/plain</option>
        </select>
      </div>
    </div>
  );
}

function WebhookActionConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Webhook URL</Label>
        <Input
          placeholder="https://webhook.site/your-unique-id"
          value={configText(node.config.url)}
          onChange={(e) => onUpdate('url', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <div>
        <Label className="text-sm">Custom Headers (JSON)</Label>
        <textarea
          placeholder='{"X-Custom-Header": "value"}'
          value={configText(node.config.headers)}
          onChange={(e) => onUpdate('headers', e.target.value)}
          className="w-full mt-1 text-xs p-2 rounded border border-input bg-background"
          rows={3}
        />
      </div>
    </div>
  );
}

function StorageActionConfig({ node, onUpdate }: any) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Storage Key</Label>
        <Input
          placeholder="workflow_result_key"
          value={node.config.key || ''}
          onChange={(e) => onUpdate('key', e.target.value)}
          className="mt-1 text-xs"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Data will be stored in browser localStorage with this key.
      </p>
    </div>
  );
}